/** Pages generated from the shared project, so an event packet can include them next to uploaded files. */
import { PDFDocument } from "pdf-lib";
import { TextKit } from "../business/pdfText";
import { scheduleText } from "@/lib/workflow/sessionOps";
import { budgetReport } from "@/lib/workflow/budgetOps";
import type { Project } from "@/lib/workflow/projectFile";

export type PacketPart = "agenda" | "guests" | "budget";
export const PACKET_PART_LABEL: Record<PacketPart, string> = { agenda: "Agenda", guests: "Guest list", budget: "Budget summary" };

const dayLabelFor = (locale: string) => (iso: string) => {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  try { return new Intl.DateTimeFormat(locale || undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }).format(d); } catch { return iso; }
};

/** The text of a part, one line per entry, or null when the project has nothing for it. */
export function packetLines(part: PacketPart, project: Project): { title: string; lines: string[] } | null {
  const { sessions, guests, expenses, budgets } = project.data;
  if (part === "agenda") {
    if (!sessions.length) return null;
    const text = scheduleText(sessions, "", dayLabelFor(project.locale));
    return { title: PACKET_PART_LABEL.agenda, lines: text.split("\n").slice(1).filter((l, i) => !(i === 0 && l === "")) };
  }
  if (part === "guests") {
    const list = guests.filter((g) => g.status !== "declined");
    if (!list.length) return null;
    const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return { title: PACKET_PART_LABEL.guests, lines: sorted.map((g) => [g.name, [g.organisation, g.role].filter(Boolean).join(", ")].filter(Boolean).join(" - ")) };
  }
  if (!budgets.length && !expenses.length) return null;
  const rep = budgetReport(budgets, expenses, project.currency);
  const f = (() => { try { return new Intl.NumberFormat(project.locale || undefined, { style: "currency", currency: project.currency, maximumFractionDigits: 2 }); } catch { return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }); } })();
  return {
    title: PACKET_PART_LABEL.budget,
    lines: [...rep.rows.map((r) => `${r.category}: planned ${f.format(r.planned)}, spent ${f.format(r.spent)}, left ${f.format(r.remaining)}`), "", `Total: planned ${f.format(rep.planned)}, spent ${f.format(rep.spent)}, left ${f.format(rep.remaining)}`],
  };
}

/** A plain titled list as a PDF; long lists continue on more pages. */
export async function buildListPdf(title: string, lines: string[], page: "letter" | "a4" = "letter"): Promise<Uint8Array> {
  const [W, H] = page === "a4" ? [595.28, 841.89] : [612, 792];
  const M = 54;
  const pdf = await PDFDocument.create();
  const kit = await TextKit.create(pdf, [title, ...lines, "0123456789 .,:;-()%$"].join("\n"));
  let pg = pdf.addPage([W, H]);
  let y = H - M;
  kit.draw(pg, title, { x: M, y, size: 20, bold: true });
  y -= 34;
  for (const raw of lines) {
    const wrapped = raw ? kit.wrap(raw, 11, W - 2 * M) : [""];
    if (y - wrapped.length * 15 < M) { pg = pdf.addPage([W, H]); y = H - M; }
    for (const l of wrapped) { if (l) kit.draw(pg, l, { x: M, y, size: 11 }); y -= 15; }
    y -= 3;
  }
  return pdf.save();
}
